import { useCurrency } from "../currency/Currency";
import { checkoutApi, readSession, errorMessage, assertCartEditable } from "../checkout/client";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { DataId, UserInfo, cartItems } from "./atoms/atoms";
import { Link, useNavigate } from "react-router-dom";
import "../cart.css";
import { gaItem, trackEvent } from "../analytics/tracking";

const limit = (item) =>
  Number.isSafeInteger(item.totalQantity)
    ? Math.max(0, Math.min(99, item.totalQantity))
    : 99;
const valid = (item) =>
  Number.isSafeInteger(item.quantity) &&
  item.quantity >= 1 &&
  item.quantity <= limit(item) &&
  Number.isFinite(item.price) &&
  item.price >= 0;
export default function Cart() {
  const { money, currency } = useCurrency();
  const user = useRecoilValue(UserInfo);
  const [cart, setCart] = useRecoilState(cartItems);
  const setDataId = useSetRecoilState(DataId);
  const [editing, setEditing] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const signedIn = !!(user?.access_token && user.user?._id);
  const locked = Boolean(readSession()?.locked);
  const invalid = cart.some((item) => !valid(item) || (item.option?.length > 0 && !item.optionId));
  const cents = cart.reduce(
    (sum, item) =>
      sum + (valid(item) ? Math.round(item.price * 100) * item.quantity : 0),
    0
  );
  const count = cart.reduce(
    (sum, item) =>
      sum +
      (Number.isSafeInteger(item.quantity) && item.quantity > 0
        ? item.quantity
        : 0),
    0
  );
  const checkout = useMutation(
    async () => {
      if (!signedIn) throw new Error("Sign in before starting checkout.");
      const existing = readSession();
      const sameAccount = existing && !existing.guestToken && existing.ownerId === user.user._id;
      if (existing?.locked) {
        if (!sameAccount) throw new Error("This unfinished checkout belongs to a guest or another account. Sign in to the original account, or contact us for help before starting another payment.");
        return existing;
      }
      if (!cart.length || invalid)
        throw new Error("Review your cart before continuing.");
      if (sameAccount) {
        await checkoutApi.update(existing, cart, user);
        return existing;
      }
      return checkoutApi.create(cart, user);
    },
    {
      onSuccess: (session) => {
        setDataId(session.cartId);
        navigate("/checkout");
      },
      onError: (err: any) => {
        if (err?.response?.status === 401) {
          navigate("/login", { state: { returnTo: "/cart", checkoutRequired: true } });
          window.scrollTo({ top: 0, left: 0, behavior: "auto" });
        } else setError(errorMessage(err));
      },
    }
  );
  async function change(item, quantity: number | null) {
    setError("");
    if (editing || checkout.isLoading) {
      setError(
        "Please wait while your cart is updated."
      );
      return;
    }
    if (
      quantity !== null &&
      (!Number.isSafeInteger(quantity) ||
        quantity < 1 ||
        quantity > limit(item))
    ) {
      setError("Choose a quantity within the available stock.");
      return;
    }
    setEditing(true);
    try { await assertCartEditable(user); } catch (cause) { setError(errorMessage(cause)); setEditing(false); return; }
    setEditing(false);
    setCart((current) =>
      quantity === null
        ? current.filter((p) => !(p.id === item.id && (p.optionId||'') === (item.optionId||'')))
        : current.map((p) =>
            p.id === item.id && (p.optionId||'') === (item.optionId||'')
              ? {
                  ...p,
                  quantity,
                  subtotal: (Math.round(p.price * 100) * quantity) / 100,
                }
              : p
          )
    );
    setMessage(
      quantity === null
        ? `${item.name} removed from your cart.`
        : `Quantity updated for ${item.name}.`
    );
  }
  return (
    <main className="sc-page">
      <Link className="sc-back" to="/products">
        ← Continue shopping
      </Link>
      <header className="sc-heading">
        <div>
          <p>YOUR SELECTION</p>
          <h1>Your cart</h1>
        </div>
        <span>
          {count} {count === 1 ? "item" : "items"} · {currency}
        </span>
      </header>
      {locked && (
        <div className="sc-notice" role="status">
          You can change items here. We will cancel the unfinished payment before applying your changes. <Link to="/checkout">Return to checkout →</Link>
        </div>
      )}
      {cart.some((item) => item.tags?.includes("staging-demo")) && (
        <p className="sc-sample">
          STAGING PREVIEW · This cart contains sample products and prices for
          testing only.
        </p>
      )}
      {error && (
        <p className="sc-error" role="alert">
          {error}
        </p>
      )}
      {message && (
        <p className="sc-message" role="status">
          {message}
        </p>
      )}
      {!cart.length ? (
        <section className="sc-empty">
          <span aria-hidden="true">＋</span>
          <h2>Your cart is empty.</h2>
          <p>Explore parts and accessories for your gaming machine.</p>
          <Link className="sc-primary" to="/products">
            Browse products →
          </Link>
        </section>
      ) : (
        <div className="sc-layout">
          <section aria-label="Cart items" className="sc-items">
            {cart.map((item) => (
              <article className="sc-item" key={`${item.id}:${item.optionId||''}`}>
                <Link
                  className="sc-image"
                  to={`/product/${encodeURIComponent(item.id)}`}
                >
                  <img
                    src={item.Image || item.image || "/catalog/placeholder.svg"}
                    alt={item.name}
                    onError={(e) => {
                      if (
                        !e.currentTarget.src.endsWith(
                          "/catalog/placeholder.svg"
                        )
                      )
                        e.currentTarget.src = "/catalog/placeholder.svg";
                    }}
                  />
                </Link>
                <div className="sc-item-info">
                  <h2>
                    <Link to={`/product/${encodeURIComponent(item.id)}`}>
                      {item.name}
                    </Link>
                  </h2>
                  <p>
                    {Number.isFinite(item.price)
                      ? `${money(item.price)} each`
                      : "Price unavailable"}
                  </p>
                  {(!valid(item) || (item.option?.length > 0 && !item.optionId)) && (
                    <p className="sc-line-error">
                      Review this product’s quantity or options before checkout.
                    </p>
                  )}
                  <div className="sc-controls">
                    <div
                      className="sc-quantity"
                      role="group"
                      aria-label={`Quantity for ${item.name}`}
                    >
                      <button
                        aria-label={`Decrease ${item.name}`}
                        disabled={
                          editing || checkout.isLoading || item.quantity <= 1
                        }
                        onClick={() => change(item, item.quantity - 1)}
                      >
                        −
                      </button>
                      <output aria-label={`Quantity of ${item.name}`}>
                        {item.quantity}
                      </output>
                      <button
                        aria-label={`Increase ${item.name}`}
                        disabled={
                          editing ||
                          checkout.isLoading ||
                          item.quantity >= limit(item)
                        }
                        onClick={() => change(item, item.quantity + 1)}
                      >
                        ＋
                      </button>
                    </div>
                    <button
                      className="sc-remove"
                      aria-label={`Remove ${item.name}`}
                      disabled={editing || checkout.isLoading}
                      onClick={() => change(item, null)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
                <strong className="sc-line-total">
                  {valid(item)
                    ? money(
                        (Math.round(item.price * 100) * item.quantity) / 100
                      )
                    : "—"}
                </strong>
              </article>
            ))}
            <p className="sc-muted">
              Quantity changes save automatically. Availability and prices are
              confirmed at checkout.
            </p>
          </section>
          <aside className="sc-summary" aria-label="Order summary">
            <h2>Order summary</h2>
            <dl>
              <div>
                <dt>Item subtotal</dt>
                <dd>{invalid ? "Review items" : money(cents / 100)}</dd>
              </div>
              <div>
                <dt>Delivery</dt>
                <dd>Selected at checkout</dd>
              </div>
            </dl>
            <p className="sc-delivery">
              Free store pickup available. Courier charges may be payable
              separately.
            </p>
            <button
              className="sc-primary"
              disabled={editing || checkout.isLoading || (!locked && invalid)}
              onClick={() => {
                setError("");
                if (!signedIn) {
                  navigate("/login", { state: { returnTo: "/cart", checkoutRequired: true } });
                  window.scrollTo({ top: 0, left: 0, behavior: "auto" });
                  return;
                }
                trackEvent("begin_checkout", { currency: "USD", value: cents / 100, items: cart.map(gaItem) });
                checkout.mutate();
              }}
            >
              {checkout.isLoading
                ? "Preparing checkout…"
                : !signedIn
                ? "Sign in to check out →"
                : locked
                ? "Resume payment →"
                : "Continue to checkout →"}
            </button>
            <p className="sc-muted">
              {currency === "JMD"
                ? "JMD display amounts are estimates. Card payments use USD; JMD bank transfers use the amount locked at checkout."
                : "Review your delivery option and final total before payment."}
            </p>
            {!signedIn && <p className="sc-muted">An account is required to check out. Your cart will be saved while you sign in or create an account.</p>}
            <Link to="/contact">Need help with your order?</Link>
          </aside>
        </div>
      )}
      <section className="sc-service">
        <div>
          <strong>Free store pickup</strong>
          <p>Choose pickup during checkout.</p>
        </div>
        <div>
          <strong>Need the right part?</strong>
          <p>
            <Link to="/contact">Ask our team about compatibility.</Link>
          </p>
        </div>
      </section>
    </main>
  );
}
